import { Elements, TextButton } from '@tfo/common'
import * as React from 'react'
import { WrappedComponentProps, injectIntl, useIntl } from 'react-intl'
import { Link, useHistory } from 'react-router-dom'
import DenyApprovalModal from '../../../../components/modals/deny-approval-modal/DenyApprovalModal'
import $http from '../../../../utils/$http'
import Attachments from '../../common/attachments/Attachments'
import Instructions from '../../common/instructions/Instructions'
import { ApprovableOrderViewModel } from '../../ViewModels'

import './SignatureInformation.scss'

const { Paragraph, ListItem, UnsortedList } = Elements

type SignatureInformationProps = ApprovableOrderViewModel & {}

const SignatureInformation: React.FunctionComponent<SignatureInformationProps &
    WrappedComponentProps> = (props) => {
        const { companyName, canAttachFile } = props
        const intl = useIntl()
        const history = useHistory()
        const [needAttachments, setNeedAttachments] = React.useState<boolean>(props.files.length <= 0)
        const [needScrollToSignButton, setNeedScrollToSignButton] = React.useState<boolean>(true)
        const [showDenyApprovalModal, setShowDenyApprovalModal] = React.useState<boolean>(false)
        const [showNetworkError, setShowNetworkError] = React.useState<boolean>(false)
        const signButtonRef = React.useRef(null)

        const handleAttachmentsChange = (attachments: string[]) => {
            setNeedAttachments(attachments.length <= 0)
            scrollIntoView();
        }

        const scrollIntoView = () => {
            //scrollIntoViewIfNeeded is not a standard method, 
            //but seems to work in chromium browsers. Use that one if available
            //otherwise do scrollIntoView once to prevent "hopping" scrolling
            if(signButtonRef.current.scrollIntoViewIfNeeded) {
                signButtonRef.current.scrollIntoViewIfNeeded(true)
            } 
            else if(needScrollToSignButton) {
                signButtonRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
                setNeedScrollToSignButton(false)
            }
        }

        const startSignature = async () => {
            const signatureLink = await $http.post<{ link: string }>(`signature/${props.orderId}/start`, null)
            window.location.href = signatureLink.link
        }

        const denyOrder = async () => {
            $http.post(`signature/deny/${props.orderId}`, null).then(() => {
                setShowNetworkError(false)
                history.goBack()
            }).catch(() => {
                setShowNetworkError(true)
            })
        }

        return <div className='signature-information'>
            <div className="AboutBackgroundCheck">
                <Paragraph>
                    {
                        props.intl.formatMessage(
                            { id: 'startIndividual.signature.body' },
                            { companyName })
                    }
                </Paragraph>
            </div>
            <div>
                <div className="todo-list">
                    <UnsortedList>
                        <ListItem>{intl.formatMessage({ id: 'startIndividual.signature.body.point1' })}</ListItem>
                        <ListItem>{intl.formatMessage({ id: 'startIndividual.signature.body.point2' })}</ListItem>
                    </UnsortedList>
                    <Paragraph>{intl.formatMessage(
                        { id: 'startIndividual.signature.GdprPolicySummary' },
                        {
                            link: (
                                <a
                                    href={props.intl.formatMessage({ id: 'common.tfoGdprPolicy.link-url' })}
                                    target='_blank' rel="noreferrer">
                                    {props.intl.formatMessage({ id: 'common.tfoGdprPolicy' })}{' '}
                                </a>
                            )
                        })}</Paragraph>
                </div>

                {props.instructions && <Instructions {...props} />}
                <Attachments {...props} onAttachmentsChange={handleAttachmentsChange} highlighted={needAttachments} />
            </div>

            <div className={'IndividualButtons'} >
                <TextButton
                    secondary={true}
                    className="StartIndividual__TextButton StartIndividual__TextButton--left"
                    onClick={() => {
                        setShowDenyApprovalModal(true)
                    }}>
                    {props.intl.formatMessage({ id: 'startIndividual.btn.deny' })}
                </TextButton>
                <div className='sign-with-scrive' ref={signButtonRef}>
                    <TextButton
                        disabled={needAttachments}
                        onClick={startSignature}
                        className="StartIndividual__TextButton StartIndividual__TextButton--right">
                        {props.intl.formatMessage({ id: 'startIndividual.scrive.btn.approve' })}
                    </TextButton>
                    <Paragraph small>
                        {props.intl.formatMessage({ id: 'awaitResponse.signApprovalButton.description' })}
                    </Paragraph>
                </div>
            </div>
            <div className="AboutBackgroundCheck">
                {showNetworkError &&
                    <div className="InputErrorMessage">
                        {props.intl.formatMessage({ id: 'startIndividual.networkError' })}
                    </div>
                }
            </div>
            <div className="AboutBackgroundCheck">
                <Paragraph small >
                    {props.intl.formatMessage(
                        { id: 'startIndividual.footer' },
                        {
                            companyName: companyName,
                            link: (
                                <Link to="/faq">
                                    {props.intl.formatMessage({
                                        id: 'startIndividual.link.faq',
                                    })}
                                </Link>
                            ),
                        },
                    )}
                </Paragraph>
            </div>

            {showDenyApprovalModal ? (
                <DenyApprovalModal
                    onDenyClick={() => setShowDenyApprovalModal(false)}
                    onCloseClick={() => setShowDenyApprovalModal(false)}
                    onConfirmClick={denyOrder}
                    companyName={companyName}
                />
            ) : null}
        </div>
    }

export default injectIntl(SignatureInformation)